import { Box, FormLabel } from '@ksyos/design-system';
import { useState } from 'react';
import type { ICheckedTreatment } from '@common/interfaces/checkedTreatment';
import type { ITherapielandTreatment } from '../../../../../../common/utils/therapielandTreatments';
import { RoundedCheckMarkButton } from '../../../../elements/buttons/roundedCheckMarkButton/RoundedCheckMarkButton';
import { ButtonContainer } from '../../../../elements/common';
import { getCategoryName } from './GgzbTherV1TreatmentListModal';

interface IGgzbTherV1ThemeButtonsProps {
    setCheckedTreatments: (treatmentsToCheck: ICheckedTreatment[], idsToUncheck: number[]) => void;
    treatments: ITherapielandTreatment[];
    treatmentsVisible: ITherapielandTreatment[];
}

enum ThemeName {
    Angst = 'Angst',
    OverspanningEnBurnOut = 'Overspanning & Burn-out',
    Somberheid = 'Somberheid',
    Stress = 'Stress',
    Zelfbeeld = 'Zelfbeeld',
}

const themes = [
    { name: ThemeName.Stress, treatments: ['Leefstijl', 'Mindfulness', 'StressLes', 'Ontspanning', 'Balans'] },
    {
        name: ThemeName.OverspanningEnBurnOut,
        treatments: ['Overspanning & burn-out', 'Mindfulness', 'Lekker slapen', 'Piekeren'],
    },
    {
        name: ThemeName.Zelfbeeld,
        treatments: ['Zelfbeeld', 'Zelfcompassie: minder streng voor jezelf', 'Opkomen voor jezelf'],
    },
    {
        name: ThemeName.Somberheid,
        treatments: ['Depressie – CGT', 'Somberheid', 'Activatie', 'G-schema - Uitdaagvragen'],
    },
    { name: ThemeName.Angst, treatments: ['Paniek', 'Gegeneraliseerde angst', 'Piekeren', 'Omgaan met emoties'] },
];

export function GgzbTherV1ThemeButtons(props: IGgzbTherV1ThemeButtonsProps) {
    const [checkedThemeName, setCheckedThemeName] = useState<ThemeName | null>(null);

    if (!getVisibleThemes().length) {
        return null;
    }

    return (
        <Box
            display="flex"
            flexDirection="column"
            mb={4}
            mt={6}
        >
            <FormLabel
                sx={{ mb: 2 }}
                tooltipText="Klik op een thema voor bijpassende programmasuggesties"
            >
                Thema's
            </FormLabel>
            <ButtonContainer
                buttons={getThemeButtons()}
                noMargin
                wrapContent
            />
        </Box>
    );

    function getThemeButtons() {
        const buttons: JSX.Element[] = [];
        getVisibleThemes().forEach((theme) => {
            buttons.push(
                <RoundedCheckMarkButton
                    isChecked={checkedThemeName === theme.name}
                    onClick={() => onClickTheme(theme.name, theme.treatments)}
                >
                    {theme.name}
                </RoundedCheckMarkButton>
            );
        });

        return buttons;
    }

    function getVisibleThemes() {
        if (props.treatments.length === props.treatmentsVisible.length) {
            return themes;
        }

        const visibleThemes = [];
        const visbibleTreatmentNames = props.treatmentsVisible.map((visibleTreatment) => visibleTreatment.name);
        for (const theme of themes) {
            const themeButtonCanBeVisible = theme.treatments.some((treatmentName) =>
                visbibleTreatmentNames.includes(treatmentName)
            );
            if (themeButtonCanBeVisible) {
                visibleThemes.push(theme);
            }
        }

        return visibleThemes;
    }

    function onClickTheme(themeName: ThemeName, themeTreatments: string[]) {
        let treatmentIdsToUncheck: number[] = [];
        let treatmentsToCheck: ICheckedTreatment[] = [];

        if (checkedThemeName) {
            treatmentIdsToUncheck = getTreatmentIdsToUncheck(checkedThemeName);
        }

        if (checkedThemeName !== themeName) {
            treatmentsToCheck = getTreatmentsToCheck(themeTreatments);
            setCheckedThemeName(themeName);
        } else {
            setCheckedThemeName(null);
        }

        props.setCheckedTreatments(treatmentsToCheck, treatmentIdsToUncheck);
    }

    function getTreatmentsToCheck(themeTreatments: string[]) {
        const treatmentsToCheck: ICheckedTreatment[] = [];
        props.treatments.forEach((treatment) => {
            if (
                themeTreatments
                    .map((themeTreatment) => themeTreatment.toLowerCase())
                    .includes(treatment.name.toLowerCase())
            ) {
                treatmentsToCheck.push({
                    id: treatment.id,
                    treatmentName: treatment.name,
                    category: getCategoryName(treatment.categories),
                });
            }
        });

        return treatmentsToCheck;
    }

    function getTreatmentIdsToUncheck(themeName: ThemeName) {
        const treatmentNamesForTheme = themes
            .find((theme) => theme.name === themeName)
            ?.treatments.map((treatment) => treatment.toLowerCase());
        if (!treatmentNamesForTheme) {
            throw new Error('Something went wrong retrieving the theme treatments');
        }

        const treatmentIdsToUncheck: number[] = [];
        props.treatments.forEach((treatment) => {
            if (treatmentNamesForTheme.includes(treatment.name.toLowerCase())) {
                treatmentIdsToUncheck.push(treatment.id);
            }
        });

        return treatmentIdsToUncheck;
    }
}
